import React, {useCallback, useMemo, useRef, useState} from 'react';
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import InputDateTime from "../../../components/input-datetime";
import InputForm from "../../../components/input-form";
import Button from "../../../components/button";
import {FaPlus} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import RemoveButtonCellRenderer from "../../../components/cell-renderer/remove-button-cell-renderer";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import InputText from "../../../components/input-text";
import InputCheckbox from "../../../components/input-checkbox";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import moment from "moment";
import {useQuery} from "@tanstack/react-query";
import activityControlService from "../../../services/activity-control-service";
import {useGlobalKey} from "../../../components/use-global-key";
import softwareAttendanceService from "../../../services/software-attendance-service";
import {statusList} from "../../software-attendances/resources/statusList";

const formatAttendanceStatus = (status) => {
    const normalizedStatus = status?.toUpperCase();

    return statusList.find(({value}) => value === normalizedStatus)?.label ?? status ?? "";
};

const initialState = {
    date: new Date(),
    observation: "",
    gitlab: [],
    waiting: false,
}
const ActivityControlForm = () => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get("duplicate-id");
    const navigate = useNavigate();
    const [state, setState] = useState(initialState)
    const [gitlabDescription, setGitlabDescription] = useState("");
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState(null)
    const gitlabRef = useRef();
    const defaultColDef = useMemo(() => ({resizable: true}), []);

    const {isLoading: isLoadingDuplicate, error: duplicateError} = useQuery({
        queryFn: () => activityControlService.getById(duplicateId).then(({data}) => {
            const gitlab = JSON.parse(data.gitlab)

            setTitle(null)
            setItems(gitlab);
            setState(data)
            return data;
        }),
        queryKey: ['activity-control-duplicate', {duplicateId}],
        enabled: !!duplicateId,
        retry: 0,
    });
    if (duplicateError) {
        duplicateError.status === 403 ? navigate("/403") : navigate("/404");
    }

    const {isLoading, refetch, error} = useQuery({
        queryFn: () => activityControlService.getById(id).then(({data}) => {
            const {created_at_formated, user_created_name, updated_at_formated, user_updated_name} = data

            const constructedTitle = [
                (created_at_formated && user_created_name) ? `Cadastrado em ${created_at_formated} por ${user_created_name}` : null,
                (updated_at_formated && user_updated_name) ? `Última atualização em ${updated_at_formated} por ${user_updated_name}` : null
            ].filter(Boolean).join(' - ');

            setTitle(constructedTitle)
            const gitlab = JSON.parse(data.gitlab)

            setItems(gitlab);
            setState(data)
            return data;
        }),
        queryKey: ['productivity', {id}],
        enabled: !!id,
    });
    if (error) {
        error.status === 403 ? navigate("/403") : navigate("/404");
    }
    const onCellClicked = (params) => {
        const {event, data, colDef, value} = params;

        if (colDef.field === "issues" && value){
            window.open(value)
        }

        if (colDef.field === "id" || colDef.field === "status" || colDef.field === "project_name"){
            event.ctrlKey && event.button === 0 ? window.open(`/software-attendances/form/${data.id}`) : navigate(`/software-attendances/form/${data.id}`);
        }
    };

    const {data: dailyAttendances} = useQuery({
        queryFn: () => softwareAttendanceService.dailyAttendance({date: state.date, user: state.user_creation}),
        queryKey: ['dailyAttendance', state.date],
        initialData: [],
        enabled: !!id,
    })

    const [columnDefs] = useState([
        {headerName: "Issues", field: "issues", flex: 1, minWidth: 250, sortable: true},
        {
            field: "",
            sortable: false,
            width: 70,
            editable: false,
            cellRenderer: RemoveButtonCellRenderer,
        },
    ]);

    const [softwareColumnDefs] = useState([
        {headerName: "ID", field: "id", width: 75, sortable: true},
        {headerName: "Status", field: "status", flex: 1, minWidth: 250, sortable: true,
            valueFormatter: ({value}) => formatAttendanceStatus(value),
        },
        {headerName: "Projeto", field: "project_name", flex: 1, minWidth: 250, sortable: true},
    ]);

    const handleDateInput = (value, name) => setState(prev => ({...prev, [name]: value}));
    const handleInput = ({target: {id, value}}) => setState(prev => ({...prev, [id]: value}));

    const addItem = useCallback(() => {
        if (!gitlabDescription) {
            toast.error(AlertMessage.generalEmpty);
            return
        }

        gitlabRef.current?.api.applyTransaction({add: [{issues: gitlabDescription}]});
        setGitlabDescription("");
    }, [gitlabRef, gitlabDescription]);

    const cantSubmit = (gitlab) => {
        return state.waiting === false && gitlab.length === 0 && (state.observation === "" || state.observation === null)
    }

    const submit = () => {
        const formData = new FormData();

        const form = {
            date: state.date ? moment(state.date).format("YYYY-MM-DD") : "",
            observation: state.observation ?? "",
            waiting: state.waiting ?? false,
        }

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        const itemsRow = [];
        gitlabRef.current?.api.forEachNode((node) => itemsRow.push(node.data));

        if (cantSubmit(itemsRow)){
            return toast.error(AlertMessage["fillSomeField"]);
        }

        const jsonItems = JSON.stringify(itemsRow);
        formData.append("gitlab", jsonItems);

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? activityControlService.edit(formData, id) : activityControlService.create(formData);

        response
            .then(({ data }) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/activity-control/form/${data.id}`);
            })
            .catch((err) => {
                const { data: { message } } = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
    }

    const duplicate = () => {
        navigate(`/activity-control/form?duplicate-id=${id}`);
    }

    // useGlobalKey("Enter", (e) => {
    //     const isGitlabFocused = document.activeElement?.id === "gitlab";
    //     const isObservationFocused = document.activeElement?.id === "observation";
    //
    //     if (isObservationFocused) return
    //     e.preventDefault();
    //
    //     if (isGitlabFocused) {
    //         addItem();
    //     } else {
    //         submit();
    //     }
    // });

    useGlobalKey("Escape", () => {
        const focusedEl = document.activeElement;

        if (focusedEl instanceof HTMLElement) {
            focusedEl.blur();
        }
    });

    return (
        <TemplateDefault title="Cadastro de  atividades" isLoading={isLoading || isLoadingDuplicate}>
            <Card title={title} headerClass={title ? "text-center" : ""} titleClass={title ? "text-sm" : "text-lg"} wrapperClass={"relative mt-2"}>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">

                    <div className="flex items-center justify-between w-full col-span-12">

                        <div>
                            <InputDateTime
                                id="date"
                                name="date"
                                label="Data *"
                                selected={state.date}
                                onChange={(value) => handleDateInput(value, "date")}
                            />
                        </div>

                        <div>
                            <InputCheckbox
                                id="waiting"
                                name="waiting"
                                label="Estou sem tarefa."
                                onChange={({target: {checked}}) => setState(prev => ({
                                    ...prev,
                                    waiting: checked
                                }))}
                                checked={state.waiting}
                            />
                        </div>

                    </div>

                    <div className={"flex col-span-12 gap-4"}>
                        <InputForm id="gitlab" label="Issue" col="w-full" value={gitlabDescription}
                                   onChange={({target: {value}}) => setGitlabDescription(value)}/>
                        <Button onClick={addItem} target="primary">
                            <FaPlus/>
                        </Button>
                    </div>

                    <div className={"ag-theme-quartz grid col-span-12"} style={{ height: "200px" }}>
                        <AgGridReact
                            ref={gitlabRef}
                            rowHeight={32}
                            rowData={items}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            animateRows={false}
                            suppressRowClickSelection={true}
                            suppressCellFocus={true}
                            gridOptions={{ localeText: LocalizationAgGrid }}
                            onCellClicked={onCellClicked}
                        />
                    </div>

                    <InputText
                        onChange={handleInput}
                        height={`${dailyAttendances.length > 0 ? "h-[160px]" : "h-[calc(100vh-535px)]"}`}
                        id="observation"
                        name="observation"
                        label="Observação"
                        col="col-span-12 mt-1"
                        value={state.observation}
                    />
                </div>
            </Card>

            { dailyAttendances.length > 0 &&
                <Card title={"Suporte - Atendimentos"} wrapperClass={"mt-4"}>
                        <div className={"ag-theme-quartz grid col-span-12"} style={{height: "200px"}}>
                            <AgGridReact
                                rowHeight={32}
                                rowData={dailyAttendances}
                                columnDefs={softwareColumnDefs}
                                defaultColDef={defaultColDef}
                                animateRows={false}
                                suppressRowClickSelection={true}
                                suppressCellFocus={true}
                                gridOptions={{localeText: LocalizationAgGrid}}
                                onCellClicked={onCellClicked}
                            />
                        </div>
                </Card>
            }

            <Card wrapperClass={"mt-4"}>
                <div className="flex justify-end flex-wrap gap-3">
                    {
                        id &&
                        <Button onClick={duplicate} target="primary">
                            Duplicar
                        </Button>
                    }
                    <Button onClick={() => navigate("/activity-control")} target="warning">
                        Cancelar
                    </Button>
                    <Button onClick={() => submit()} target="success">
                        Salvar
                    </Button>
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default ActivityControlForm;
